import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { API_BASE_URL, getAuthHeaders, getCustomerId, getStoredUser } from '../auth';
import { EmptyPane, ErrorBanner, LoadingPane, PageHeader } from '../components/page-elements';

const RailcarUnloads = () => {
  const user = useMemo(() => getStoredUser(), []);
  const customerId = getCustomerId(user);
  const isCustomer = user?.role === 'customer';

  const [summary, setSummary] = useState([]);
  const [bols, setBols] = useState([]);
  const [selectedRailcar, setSelectedRailcar] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const params = new URLSearchParams();
    if (isCustomer && customerId) params.set('customerId', customerId);

    fetch(`${API_BASE_URL}/api/bols/railcars/unloads${params.toString() ? `?${params.toString()}` : ''}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch railcar summary');
        return res.json();
      })
      .then((data) => {
        setSummary(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [isCustomer, customerId]);

  useEffect(() => {
    if (!selectedRailcar) {
      setBols([]);
      return;
    }

    const params = new URLSearchParams();
    params.set('status', 'Completed');
    params.set('railcarID', selectedRailcar);
    if (isCustomer && customerId) params.set('customerId', customerId);

    fetch(`${API_BASE_URL}/api/bols?${params.toString()}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch unload details');
        return res.json();
      })
      .then((data) => setBols(data))
      .catch((err) => setError(err.message));
  }, [selectedRailcar, isCustomer, customerId]);

  if (loading) return <LoadingPane label="Loading railcar unloads..." />;

  return (
    <Box p={2}>
      <PageHeader title="Railcar Unloads" subtitle="View completed unload history by railcar." />
      <ErrorBanner message={error} />

      {summary.length === 0 ? (
        <EmptyPane title="No completed unloads found" />
      ) : (
        <>
          {isMobile ? (
            <Stack spacing={1.2} sx={{ mb: 2 }}>
              {summary.map((row) => (
                <Card key={row.railcarID} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={700}>{row.railcarID}</Typography>
                    <Typography variant="body2" color="text.secondary">Unloads: {row.unloadCount}</Typography>
                    <Typography variant="body2" color="text.secondary">Net: {row.totalNetWeight?.toLocaleString?.() || row.totalNetWeight}</Typography>
                    <Typography variant="body2" color="text.secondary">Tons: {row.totalTons?.toFixed ? row.totalTons.toFixed(2) : row.totalTons}</Typography>
                    <Typography variant="body2" color="text.secondary">Last: {row.latestUnloadAt ? new Date(row.latestUnloadAt).toLocaleString() : 'N/A'}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer component={Paper} sx={{ mb: 3, borderRadius: 2, border: '1px solid var(--ui-border)' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#ffffff' }}>
                    <TableCell>Railcar</TableCell>
                    <TableCell>Unload Count</TableCell>
                    <TableCell>Total Net Weight</TableCell>
                    <TableCell>Total Tons</TableCell>
                    <TableCell>Last Unloaded</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.map((row) => (
                    <TableRow key={row.railcarID}>
                      <TableCell>{row.railcarID}</TableCell>
                      <TableCell>{row.unloadCount}</TableCell>
                      <TableCell>{row.totalNetWeight?.toLocaleString?.() || row.totalNetWeight}</TableCell>
                      <TableCell>{row.totalTons?.toFixed ? row.totalTons.toFixed(2) : row.totalTons}</TableCell>
                      <TableCell>{row.latestUnloadAt ? new Date(row.latestUnloadAt).toLocaleString() : 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <FormControl sx={{ minWidth: 300, mb: 2 }}>
            <InputLabel id="railcar-select-label">Select Railcar</InputLabel>
            <Select
              labelId="railcar-select-label"
              value={selectedRailcar}
              label="Select Railcar"
              onChange={(e) => setSelectedRailcar(e.target.value)}
            >
              {summary.map((row) => (
                <MenuItem key={row.railcarID} value={row.railcarID}>
                  {row.railcarID}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedRailcar && (
            <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid var(--ui-border)' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#ffffff' }}>
                    <TableCell>Order Number</TableCell>
                    <TableCell>Truck ID</TableCell>
                    <TableCell>Trailer ID</TableCell>
                    <TableCell>Net Weight</TableCell>
                    <TableCell>Ton Weight</TableCell>
                    <TableCell>Weigh Out Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bols.map((bol) => (
                    <TableRow key={bol._id}>
                      <TableCell>{bol.orderNumber?.orderNumber || 'N/A'}</TableCell>
                      <TableCell>{bol.truckID || 'N/A'}</TableCell>
                      <TableCell>{bol.trailerID || 'N/A'}</TableCell>
                      <TableCell>{bol.netWeight || 'N/A'}</TableCell>
                      <TableCell>{bol.tonWeight || 'N/A'}</TableCell>
                      <TableCell>{bol.weighOutTime ? new Date(bol.weighOutTime).toLocaleString() : 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default RailcarUnloads;

